import { useState, useCallback, useMemo, useEffect, Suspense, lazy, useRef } from 'react';
import styles from './LessonLibrary.module.scss';
import {
  useAiSearchLessonLibraryLazyQuery,
  useSearchLessonLibraryLazyQuery,
  useGetProgramLazyQuery,
} from '@api/graphql-codegen';
import { Alert } from 'antd';
import { isAiSearchUnavailable } from '../ai-search/aiSearch';
import SimulateSchool from '../ai-search/SimulateSchool';
import { SimulateSchoolState } from '../ai-search/useSimulateSchool';
import Spinner from 'src/components/spinner/Spinner';
import Header from 'src/components/header/Header';
import { Events, useAnalytics, useIsDemoAccount, useQueryParams, useUser } from '@hooks';
import CloseIcon from 'src/assets/icons/close.svg?react';
import IconRotating from 'src/assets/icons/rotating_arrow.svg?react';
import { LessonLibraryEnum } from '@types';
import { useHistory } from 'react-router-dom';
import {
  standaloneLessonsSelCompetencies,
  categoryTiersOptions,
  naAllGradeLevels,
  lessonTypeOptions,
  lessonLibraryTypeOptions,
  ProgramID,
  auAllYearLevels,
  auGettingStartedGradeLevels,
  newGettingStartedGradeLevels,
} from '@constants';
import NavLayout from './navLayout/NavLayout';
import SearchLayout from './searchLayout/SearchLayout';
import { hasArrayValues, resetGoogleTranslateWidget, sortByPosition } from '@src/utils/helpers';
import Button from '@src/components/button/Button';
import { debounce } from '@src/utils/helpers';
import _ from "lodash";
import Programs from './Programs';
const GradeItem = lazy(() => import('./gradeItem/GradeItem'));
const GradeSearchItem = lazy(() => import('./gradeItem/GradeSearchItem'));

type SearchStates = {
  searchText: string[]
  lessonType: string[]
  competency: string[]
  tier: string[]
  skill: string[]
  mood: string[]
}
import FooterPage from '@src/components/footer-page/FooterPage';

const defaultStates: SearchStates = {
  searchText: [],
  lessonType: [],
  competency: [],
  tier: [],
  skill: [],
  mood: [],
}

// OP-3164: example searches offered on the AI search page.
const AI_SEARCH_SUGGESTIONS = [
  'student struggling with friendships',
  "kid won't come to school",
  'class is anxious about upcoming tests',
];

type Props = {
  /**
   * OP-3164: rank keyword searches with AI instead of matching the words. Everything else - the
   * filters, the grade grouping, the cards - is the library's own, so the page is the same page.
   */
  aiSearch?: boolean;
  /**
   * OP-3227: a Super Admin running AI search as a chosen school. Only the AI results are simulated;
   * browsing and the keyword fallback stay the Super Admin's own.
   */
  aiSimulate?: SimulateSchoolState;
};

/** Where a result set came from, so the page can say when AI search was not available. */
type ResultSource = 'keyword' | 'ai' | 'fallback';

function LessonLibrary({ aiSearch = false, aiSimulate }: Props) {
  const basePath = aiSearch ? '/lessons/ai-search' : '/lessons';
  const history = useHistory();
  const params = useQueryParams();
  const [user] = useUser();
  const isDemoAccount = useIsDemoAccount();
  const { trackEvent } = useAnalytics();
  const tab = params.get('tab');
  const searchKeyword = params.get('search-keyword');
  const [states, setStates] = useState<SearchStates>(defaultStates)
  const [filters, setFilters] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [grades, setGrades] = useState<string[]>([]);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [lessonLibraryMode, setMyCourseMode] = useState(tab === 'programs' ? LessonLibraryEnum.Programs : LessonLibraryEnum.Library)

  const [selected, setSelected] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [focused, setFocused] = useState(false);
  const [showGradeMenu, setShowGradeMenu] = useState(false);
  const [showLessonTypeMenu, setShowLessonTypeMenu] = useState<boolean>(false);
  const [showTierMenu, setShowTierMenu] = useState(false);
  const [showCompetencyMenu, setShowCompetencyMenu] = useState(false);
  const [showSkillMenu, setShowSkillMenu] = useState(false);
  const [showMoodMenu, setShowMoodMenu] = useState(false);
  const [loadOtherMCs, setLoadOtherMCs] = useState<number | null>(null);
  const urlKeywordProcessedRef = useRef(false);

  useEffect(() => {
    resetGoogleTranslateWidget();
  }, [])
  const isAUuser = useMemo(() => { return user?.id?.startsWith("AU_") ? true : false }, [user?.id]);
  // When simulating a school, its grades and its region's grade order, not the Super Admin's own.
  const simulatedSchool = aiSearch ? aiSimulate?.target : undefined;
  const gradeOptions = useMemo(() => {
    const grades = JSON.parse((simulatedSchool ? simulatedSchool.grades : user?.schools?.[0]?.grades) || '[]');
    const auGrades = simulatedSchool ? simulatedSchool.id.startsWith('AU_') : isAUuser;
    const sortedGrades = grades.sort((a, b) => {
      if (auGrades) {
        return auGettingStartedGradeLevels.indexOf(a) - auGettingStartedGradeLevels.indexOf(b);
      }
      return newGettingStartedGradeLevels.indexOf(a) - newGettingStartedGradeLevels.indexOf(b);
    })
    return sortedGrades.map(grade => {
      return {
        label: grade,
        value: grade
      }
    })
  }, [user, isAUuser, simulatedSchool])

  const [getProgram, { data, loading }] = useGetProgramLazyQuery({ fetchPolicy: 'network-only' });
  const [searchLessonLibrary, { data: searchData, loading: searching }] = useSearchLessonLibraryLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      if (data.searchLessonLibrary) {
        trackEvent(Events.actions.lessonLibrarySearch, {
          ...data.searchLessonLibrary,
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      }
    }
  });

  const [runAiSearch, { data: aiSearchData, loading: aiSearching }] = useAiSearchLessonLibraryLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      if (data.aiSearchLessonLibrary) {
        trackEvent(Events.actions.lessonLibrarySearch, {
          ...data.aiSearchLessonLibrary,
          searchType: 'ai',
          schoolId: user?.schools?.[0]?.id,
          schoolName: user?.schools?.[0]?.name,
          userId: user?.id,
          userName: `${user?.firstName} ${user?.lastName}`,
        });
      }
    }
  });
  const [resultSource, setResultSource] = useState<ResultSource>('keyword');

  // AI search needs words to rank; a filter-only search stays a keyword search. When AI search is
  // unavailable the same search runs as a keyword search, so the page still answers.
  // The last search run, so a change of simulated school can run it again.
  const lastSearchRef = useRef<[SearchStates, string[]] | null>(null);
  const runSearch = (searchStates: SearchStates, selectedGrades: string[]) => {
    lastSearchRef.current = [searchStates, selectedGrades];
    const query = (searchStates.searchText ?? []).join(' ').trim();
    if (!aiSearch || !query) {
      setResultSource('keyword');
      return searchLessonLibrary({ variables: { input: searchStates } });
    }
    setResultSource('ai');
    return runAiSearch({
      variables: {
        input: {
          query,
          grades: selectedGrades,
          competency: searchStates.competency,
          tier: searchStates.tier,
          lessonType: searchStates.lessonType,
          skill: searchStates.skill,
          mood: searchStates.mood,
          asSchoolId: aiSimulate?.asSchoolId,
          sendAccess: aiSimulate?.sendAccess,
        },
      },
    })
      .then(({ error }) => {
        if (isAiSearchUnavailable(error)) {
          setResultSource('fallback');
          searchLessonLibrary({ variables: { input: searchStates } });
        }
      });
  };
  // The debounced call is created once, so it reaches the latest runSearch through a ref.
  const runSearchRef = useRef(runSearch);
  runSearchRef.current = runSearch;

  // Switching the simulated school, or the ranking approach, re-runs the search on screen, so the
  // results always match what the control names.
  const simulationKey = `${aiSimulate?.asSchoolId ?? ''}|${aiSimulate?.approach ?? 'new'}`;
  const firstSimulationRef = useRef(true);
  useEffect(() => {
    if (firstSimulationRef.current) {
      firstSimulationRef.current = false;
      return;
    }
    if (lastSearchRef.current) runSearchRef.current(...lastSearchRef.current);
  }, [simulationKey]);

  const debouncedApiCall = useRef(
    debounce((states: SearchStates, selectedGrades: string[] = []) => {
      if (!_.isEqual(states, defaultStates)) {
        return runSearchRef.current(states, selectedGrades)
      }
    }, 500)
  ).current;

  // Scroll to the top of the list when the component mounts
  useEffect(() => {
    const root = document.getElementById('root');
    if (root) {
      setTimeout(() => {
        root.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, []);

  useEffect(() => {
    history.replace(`${basePath}?tab=${lessonLibraryMode === LessonLibraryEnum.Programs ? 'programs' : 'library'}`);
  }, [lessonLibraryMode, history, basePath])

  useEffect(() => {
    document.body.style.overflow = showFilterMenu ? "hidden" : "auto";
    document.getElementById("lessonlibrary")!.style.overflow = showFilterMenu ? "hidden" : "auto";
  }, [showFilterMenu])

  useEffect(() => {
    if (tab === 'programs') {
      setMyCourseMode(LessonLibraryEnum.Programs)
    } else if (tab === 'library') {
      setMyCourseMode(LessonLibraryEnum.Library);

      if (isAUuser) {
        getProgram({ variables: { input: { id: ProgramID.AUSLESSONLIBRARY } } });
      } else {
        getProgram({ variables: { input: { id: ProgramID.LESSONLIBRARY } } });
      }
    }
  }, [tab, isAUuser, getProgram])

  useEffect(() => {
    const listViewElement = document.getElementById("lessonListView")
    if (listViewElement) {
      setTimeout(() => {
        listViewElement.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 500)
    }
  }, [showFilterMenu])

  const handleSelect = useCallback((gradeId: string) => {
    if (selectedId === gradeId) {
      setSelected(false);
      setSelectedId(null);
    } else {
      setSelected(true);
      setSelectedId(gradeId);
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }, [selectedId, setSelected, setSelectedId])

  const handleNoneSelect = (gradeId?: string) => {
    setSelected(false);
    setSelectedId(null);

    if (gradeId) {
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }

  const handleBackToTop = (gradeId?: string) => {
    if (gradeId) {
      setTimeout(() => {
        const courseElement = document.getElementById(gradeId);
        courseElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }, 500)
    }
  }

  const handleSelectPlanMode = useCallback((planMode: any) => {
    setMyCourseMode(planMode);
    setShowFilterMenu(false);
  }, [setShowFilterMenu]);

  const handleResetFilters = useCallback(() => {
    setFilters([]);
    setGrades([]);
    setSearch('')
    setStates(defaultStates);
  }, []);

  // Handle search keyword from URL parameter (only on first load)
  useEffect(() => {
    if (searchKeyword && searchKeyword.trim() && !urlKeywordProcessedRef.current) {
      const keyword = searchKeyword.trim();

      // Mark as processed so it only runs once
      urlKeywordProcessedRef.current = true;

      // Set the search input value
      setSearch(keyword);

      // Update states with the keyword
      const newStates = {
        ...defaultStates,
        searchText: [keyword],
      };
      setStates(newStates);

      // Add to filters
      const filterOptions = [`Keyword: ${keyword}`];
      setFilters(filterOptions);

      // Ensure we're in library mode for search results
      if (lessonLibraryMode !== LessonLibraryEnum.Library) {
        setMyCourseMode(LessonLibraryEnum.Library);
      }

      // Trigger the search immediately (not debounced for URL-based searches)
      if (!_.isEqual(newStates, defaultStates)) {
        runSearchRef.current(newStates, []);
      }
    }
  }, [searchKeyword, lessonLibraryMode])

  const handleShowFilters = () => {
    document.getElementById("lessonListView")?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    // Restore search value from filters if there are keyword filters and search is empty
    const keywordFilters = filters.filter(f => f.includes('Keyword:'));
    if (keywordFilters.length > 0 && !search) {
      // Get the last keyword filter and restore it to the search input
      const lastKeyword = keywordFilters[keywordFilters.length - 1].replace('Keyword: ', '').trim();
      if (lastKeyword) {
        setSearch(lastKeyword);
      }
    } else if (filters.length === 0 || keywordFilters.length === 0) {
      // Only clear search if there are no filters or no keyword filters
      setSearch("")
    }
    setTimeout(() => setShowFilterMenu(current => !current), 300)
  }

  const handleChangeKeyword = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const handleStatesChange = useCallback((key: string, value: any) => {
    setStates((prev) => {
      const newStates = {
        ...prev,
        [key]: value,
      }
      return newStates
    })
  }, [])

  const handleRemoveFilters = useCallback((value: string[]) => {
    const ageGroups = value.filter(group => group.includes('Grade:'))
    const ageTemp = ageGroups.map(group => {
      return group.replace('Grade: ', '')
    })
    setGrades(ageTemp)

    const changedLessonTypes = value.filter(f => f.includes('LessonType:'))
    const lessonTypeTemp = changedLessonTypes.map(lt => {
      return lt.replace('LessonType: ', '')
    })
    const lessonTypes = lessonTypeTemp.map((ltLabel) => {
      return lessonTypeOptions.find(lt => lt.label === ltLabel)?.value ?? ''
    })
    handleStatesChange('lessonType', lessonTypes ? lessonTypes : [])

    const chagedSkills = value.filter(group => group.includes('Skill:'))

    const skillTemp = chagedSkills.map(group => {
      return group.replace('Skill: ', '')
    })
    const skills = skillTemp.map((skillLabel) => {
      return standaloneLessonsSelCompetencies.find(skill => skill.label === skillLabel)?.value ?? ''
    })
    handleStatesChange('competency', skills ? skills : [])

    handleStatesChange('skill', skillTemp)

    const changedTopics = value.filter(group => group.includes('Tier:'))
    const temp = changedTopics.map(group => {
      return group.replace('Tier: ', '')
    })
    handleStatesChange('tier', temp)

    const changedMood = value.filter(group => group.includes('Mood:'))
    const moodTemp = changedMood.map(group => {
      return group.replace('Mood: ', '')
    })
    handleStatesChange('mood', moodTemp)

    const changedKeyword = value.filter(group => group.includes('Keyword:'))
    const tempKey = changedKeyword.map(group => {
      return group.replace('Keyword: ', '')
    })
    handleStatesChange('searchText', tempKey)

    if (value.length === 0) {
      handleResetFilters();
    } else {
      const searchText = value.filter(v => v.includes('Keyword:')).map(key => key.replace('Keyword:', '').trim());
      const competency = value.filter(v => v.includes('Skill:')).map(key => key.replace('Skill:', '').trim());
      const tiers = value.filter(v => v.includes('Tier:')).map(key => key.replace('Tier:', '').trim());
      const skill = value.filter(v => v.includes('Skill:')).map(key => key.replace('Skill:', '').trim());
      const mood = value.filter(v => v.includes('Mood:')).map(key => key.replace('Mood:', '').trim());
      const newSearch = {
        searchText,
        competency: isAUuser ? [] : competency,
        lessonType: lessonTypes ?? [],
        tier: tiers,
        skill: isAUuser ? skill : [],
        mood: mood,
      }
      setFilters(value);
      debouncedApiCall(newSearch, ageTemp);
    }
  }, [handleStatesChange, handleResetFilters, debouncedApiCall, isAUuser])

  const handleFilterApply = useCallback((keywordValue?: string) => {
    const searchValue = keywordValue?.trim() || search.trim();
    let filterOptions: string[] = []
    if (grades.length) {
      grades.forEach(ageGroup => {
        filterOptions.push(`Grade: ${ageGroup}`)
      })
    }
    if (states.lessonType.length) {
      const lessonTypes = states.lessonType.map((ltValue) => {
        return lessonTypeOptions.find(lt => lt.value === ltValue)?.label
      })
      lessonTypes.forEach(type => {
        filterOptions.push(`LessonType: ${type}`)
      })
    }
    if (states.competency.length) {
      const skills = states.competency.map((skillValue) => {
        return standaloneLessonsSelCompetencies.find(skill => skill.value === skillValue)?.label
      })
      skills.forEach(skill => {
        filterOptions.push(`Skill: ${skill}`)
      })
    }
    if (states.tier.length) {
      states.tier.forEach(topic => {
        filterOptions.push(`Tier: ${topic}`)
      })
    }
    if (states.skill.length) {
      states.skill.forEach(topic => {
        filterOptions.push(`Skill: ${topic}`)
      })
    }
    if (states.mood.length) {
      states.mood.forEach(topic => {
        filterOptions.push(`Mood: ${topic}`)
      })
    }
    if (hasArrayValues(states.searchText)) {
      states.searchText?.forEach(keyword => {
        filterOptions.push(`Keyword: ${keyword}`)
      })
    }
    if (searchValue) {
      if (!states.searchText?.includes(searchValue)) {
        handleStatesChange('searchText', [...states.searchText, searchValue]);
        filterOptions.push(`Keyword: ${searchValue}`);
      }
    }
    setFilters(filterOptions);
    setShowFilterMenu(false);

    // Preserve the search input value so it's visible when opening the filter menu again
    // Only clear if there's no search keyword in the filters
    if (!searchValue || filterOptions.filter(f => f.includes('Keyword:')).length === 0) {
      setSearch("");
    } else {
      // Keep the search value in the input field
      setSearch(searchValue);
    }

    const keywords = filterOptions.filter(filt => filt.includes('Keyword: ')).map(keyword => keyword.replace('Keyword: ', ''))

    var newStates = {
      ...states,
      'searchText': keywords
    }

    debouncedApiCall(newStates, grades)
  }, [grades, states, search, handleStatesChange, debouncedApiCall])

  const searchViewStyle = useMemo(() => {
    const classes = [styles.searchView];
    if (showFilterMenu) {
      classes.push(styles.filterShow);
    }
    return classes.join(' ');
  }, [showFilterMenu])

  const mainViewStyle = useMemo(() => {
    const classes = [styles.libraryView];
    return classes.join(' ');
  }, [])

  const isHasLessonLibrary = useMemo(() => {
    if (isDemoAccount) return false; // OP-3222: demo accounts only see the Programs tab
    if (user?.id?.startsWith("AU_")) {
      if (user?.schools?.[0]?.programs?.includes("CvsCvxSsD-E0Vv9ea1q1i")) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }, [isDemoAccount, user?.id, user?.schools?.[0]?.programs])

  const handleSelectGrade = useCallback((grades: string[]) => {
    setGrades(grades);
  }, [])

  const handleRemoveTag = (removedTag: string) => {
    const newTags = filters.filter((tag) => tag !== removedTag);
    handleRemoveFilters(newTags);

    // Remove search-keyword from URL if keyword tag is removed
    if (removedTag.includes('Keyword:') && searchKeyword) {
      const tabParam = lessonLibraryMode === LessonLibraryEnum.Programs ? 'programs' : 'library';
      history.replace(`${basePath}?tab=${tabParam}`);
    }
  };

  const handleClickDrop = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFilterMenu(false);
  }

  const programList = useMemo(() => {
    if (!data?.getProgram?.mcs) return [];
    var filterList = [...(data?.getProgram.mcs ?? [])].sort(sortByPosition);
    if (grades.length && !showFilterMenu) {
      filterList = filterList.filter(m => grades.includes(m?.name ?? ""))
    }
    return filterList
  }, [grades, showFilterMenu, data?.getProgram])

  const searchResults = resultSource === 'ai' ? aiSearchData?.aiSearchLessonLibrary : searchData?.searchLessonLibrary;

  const searchedList = useMemo(() => {
    if (!searchResults) return []
    var filterList = [...(searchResults ?? [])].sort(sortByPosition)
    if (grades.length && !showFilterMenu) {
      filterList = filterList.filter(m => grades.includes(m?.mCollectionName ?? ""))
    }
    return filterList;
  }, [grades, showFilterMenu, searchResults])

  const hasOnlyGradeFilters = useMemo(() => {
    return filters.every(item => item.includes('Grade:'));
  }, [filters])

  const lessonListView = (
    <div style={{ width: '100%', height: '100%' }}>
      {user &&
        <NavLayout
          showFilterMenu={showFilterMenu}
          studyMode={lessonLibraryMode}
          handleSelectPlanMode={handleSelectPlanMode}
          handleFilterClick={handleShowFilters} 
          filters={filters} 
          isAUuser={isAUuser} 
          isHasLessonLibrary={isHasLessonLibrary}
      />}
      {lessonLibraryMode === LessonLibraryEnum.Programs ? <Programs isAUuser={isAUuser} /> :
        <div className={styles.mainView}>
          <div className={mainViewStyle}>
            {aiSearch && aiSimulate && (
              <div className={styles.simulate}>
                <SimulateSchool simulate={aiSimulate} />
              </div>
            )}
            {filters.length ?
              <div className={styles.searchResult}>
                <div className={styles.searchResultTag}>
                  <span className={styles.resultTitle}>Results for</span>
                  <div className={styles.searchedTags}>
                    {filters.map((tag, index) => {
                      if (tag.includes('Grade') || tag.includes('Tier') || tag.includes('LessonType') || tag.includes('Mood')) {
                        var grade = tag.replace('Grade: ', '').replace('Tier: ', '').replace('LessonType: ', '').replace('Mood: ', '')
                        return <div
                          className={styles.searchTag}
                          key={index}
                          onClick={() => handleRemoveTag(tag)}>
                          <span>{grade}</span>
                          <CloseIcon className={styles.tagClose} />
                        </div>
                      } else if (tag.includes('Keyword')) {
                        const keyword = tag.replace('Keyword: ', '')
                        const isLongTag = keyword.length > 30;
                        return <div
                          className={styles.searchThemeTag}
                          key={index}
                          onClick={() => handleRemoveTag(tag)}
                        >
                          <span>{isLongTag ? `${keyword.slice(0, 30)}...` : keyword}</span>
                          <CloseIcon className={styles.themeTagClose} />
                        </div>
                      }
                      const isLongTag = tag.replace('Skill: ', '').length > 30;
                      return <div
                        className={styles.searchTag}
                        key={tag}
                        onClick={() => handleRemoveTag(tag)}
                      >
                        <div><span>{isLongTag ? `${tag.replace('Skill: ', '').slice(0, 30)}...` : tag.replace('Skill: ', '')}</span></div>
                        <CloseIcon className={styles.tagClose} />
                      </div>
                    })
                    }
                  </div>
                </div>
                <Button onClick={handleResetFilters} className={styles.resetBtn} label={'Reset'}
                  size={'extraSmall'} outline={true} color={'red'}
                  leftIcon={<IconRotating className={styles.resetIcon} />} />
              </div> : null
            }
            {resultSource === 'fallback' && hasArrayValues(filters) && !hasOnlyGradeFilters && !(loading || searching || aiSearching) ? (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="AI search is unavailable right now, so these results match your keywords instead."
              />
            ) : null}
            {(loading || searching || aiSearching) ? <div className={styles.lazyLoadingView}><Spinner /></div> : <div className={styles.listView}>
              {hasArrayValues(filters) && !hasOnlyGradeFilters ? (
                hasArrayValues(searchedList) ? searchedList.map((item, index) => {
                  return (
                    <Suspense key={index} fallback={<div className={styles.lazyLoadingView}><Spinner /></div>}>
                      <div style={{ position: 'relative' }}>
                        <GradeSearchItem
                          gradeId={item?.mCollectionId ?? ""}
                          gradeName={item?.mCollectionName ?? ""}
                          collectionList={item?.content || []}
                          loading={loading}
                          country={""}
                          isAUuser={isAUuser}
                        />
                      </div>
                    </Suspense>
                  )
                }) : <div className={styles.nodata}>Sorry, We couldn't find any results</div>
              ) : (programList.map((item, index) => {
                return (
                  <Suspense key={index} fallback={<div className={styles.lazyLoadingView}><Spinner /></div>}>
                    <div key={index} style={{ position: 'relative' }}>
                      <GradeItem gradeId={item?.id ?? ""} isAUuser={isAUuser}
                        gradeName={item?.name ?? ""} selected={selected} selectedId={selectedId} loading={loading}
                        handleNoneSelect={() => handleNoneSelect(item?.id)} handleSeleted={handleSelect} handleBackToTop={() => handleBackToTop(item?.id)}
                        loadOthers={loadOtherMCs} setLoadOthers={setLoadOtherMCs} index={index}
                      />
                    </div>
                  </Suspense>
                )
              })
              )}
            </div>}
          </div>
        </div>
      }
    </div>
  )

  return (
    <>
      <Header />
      <div id="lessonlibrary" className={styles.wrapper}>
        <div className={[styles.backdrop, showFilterMenu ? styles.mfadeIn : styles.mfadeOut].join(' ')} onClick={handleClickDrop} />
        <div className={searchViewStyle}>
          <SearchLayout
            focused={focused}
            setFocused={setFocused}
            showGradeMenu={showGradeMenu}
            setShowGradeMenu={setShowGradeMenu}
            showLessonTypeMenu={showLessonTypeMenu}
            setShowLessonTypeMenu={setShowLessonTypeMenu}
            showTierMenu={showTierMenu}
            setShowTierMenu={setShowTierMenu}
            showCompetencyMenu={showCompetencyMenu}
            setShowCompetencyMenu={setShowCompetencyMenu}
            filters={filters}
            search={search}
            gradeOptions={gradeOptions ? gradeOptions : isAUuser ? auAllYearLevels : naAllGradeLevels} lessonTypeOptions={lessonLibraryTypeOptions} tierOptions={categoryTiersOptions} competencyOptions={standaloneLessonsSelCompetencies}
            selGrades={grades} selLessonTypes={states.lessonType} selTiers={states.tier} selCompetencys={states.competency}
            handleSelectGrade={handleSelectGrade}
            handleSelectLessonType={(v) => handleStatesChange('lessonType', v)}
            handleSelectTier={(v) => handleStatesChange('tier', v)}
            handleSelectCompetency={(v) => handleStatesChange('competency', v)}
            handleChangeKeyword={handleChangeKeyword}
            handleApplySearch={handleFilterApply}
            isAUuser={isAUuser}
            showSkillMenu={showSkillMenu}
            setShowSkillMenu={setShowSkillMenu}
            showMoodMenu={showMoodMenu}
            setShowMoodMenu={setShowMoodMenu}
            selMood={states.mood}
            handleSelectMood={(v) => handleStatesChange('mood', v)}
            selSkill={states.skill}
            handleSelectSkills={(v) => handleStatesChange('skill', v)}
            suggestions={aiSearch ? AI_SEARCH_SUGGESTIONS : undefined}
          />
        </div>
        {(focused || showGradeMenu || showLessonTypeMenu || showCompetencyMenu || showTierMenu || showMoodMenu || showSkillMenu) && <div className={[styles.menuBackdrop, showFilterMenu ? styles.mfadeIn : styles.mfadeOut].join(' ')} />}
        <div className={styles.page}>
          {lessonListView}
        </div>
        <FooterPage />
      </div>
    </>
  );
}

export default LessonLibrary;
